import styled from '@emotion/styled';
import { useTestContext } from '../../context/testContext';
import { usePostMessage } from '../../context/messageContext';
import { Button, Slider, InfoIcon, colors, breakpoints } from '../../ui';
import { Divider, Label, ValueBox } from '../../screens/optician/testScreenKit';

// Contrast Sensitivity scoring - top half (Figma session/14 left, 23 right):
// Current Characters | Patient Preview (i) | [Send New Characters], then "Set Contrast (52.0%)" slider.

const Top = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 170px)) auto;
  justify-content: center;
  align-items: end;
  gap: 16px 24px;
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    .send {
      grid-column: 1 / -1;
      justify-self: center;
    }
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
`;

const PreviewLabel = styled(Label)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  svg {
    color: ${colors.slate[400]};
  }
`;

const SliderWrap = styled.div`
  padding: 0 4px;
  @media (max-width: ${breakpoints.phone - 1}px) {
    padding: 0;
  }
`;

const ContrastSensitivityControls = ({ contrastValue, fontColor, updateContrastSensitivityData }) => {
  const postMessage = usePostMessage();
  const {
    generateRandomCharacters,
    setContrastSensitivityConfig,
    contrastSensitivityData: {
      settings: { characters: currentCharacters },
    },
  } = useTestContext();

  const updateContrastSensitivityContent = (contrastSensitivityTestContent) => {
    postMessage({
      type: 'updateContrastSensitivityContent',
      content: contrastSensitivityTestContent,
    });
  };

  const handleContrastChange = (currentValue) => {
    updateContrastSensitivityData({ type: 'setContrastValue', payload: currentValue });
    updateContrastSensitivityData({ type: 'setFontColor', payload: `hsl(0, 0%, ${100 - currentValue}%)` });
  };

  const changeAndSendCharacters = () => {
    const newCharacters = generateRandomCharacters().substring(0, 6);
    const newData = {
      characters: newCharacters,
      fontColor: fontColor,
    };
    updateContrastSensitivityContent(newData);
    setContrastSensitivityConfig(newData);
  };

  return (
    <>
      <Top>
        <Col>
          <Label size={15} align='center'>
            Current Characters
          </Label>
          <ValueBox align='center' mono spaced fontSize={18} data-testid='cs-current-characters'>
            {currentCharacters}
          </ValueBox>
        </Col>
        <Col>
          <PreviewLabel size={15} title='What the patient sees at the current contrast'>
            Patient Preview <InfoIcon size={14} />
          </PreviewLabel>
          <ValueBox align='center' mono spaced fontSize={18} style={{ color: fontColor || '#000', borderColor: 'transparent' }} data-testid='cs-patient-preview'>
            {currentCharacters}
          </ValueBox>
        </Col>
        <div className='send'>
          <Button size='lg' onClick={() => changeAndSendCharacters()}>
            Send New Characters
          </Button>
        </div>
      </Top>
      <Divider />
      <SliderWrap>
        <Slider
          label={`Set Contrast (${contrastValue.toFixed(1)}%)`}
          value={contrastValue}
          onChange={handleContrastChange}
          min={0}
          max={100}
          step={1}
          stepBy={0.1}
          data-testid='cs-contrast-slider'
        />
      </SliderWrap>
    </>
  );
};

export default ContrastSensitivityControls;
